// Smart Sash Admin Panel - Teaching Orchestration gRPC Service
// Manages teaching session operations via gRPC Web

import {
  AccessResponse,
  CheckAccessRequest,
  GetActiveSessionsRequest,
  GetActiveSessionsResponse,
  JoinSessionRequest,
  LeaveSessionRequest,
  LeaveSessionResponse,
  ActiveSessionInfo as ProtoActiveSessionInfo,
  SessionConnectResponse,
  TeachingOrchestrationServiceDefinition,
} from '@/generated/SashAdmin/TeachingOrchestration';
import {
  ActiveSessionInfo,
  ActiveSessionsParams,
  ActiveSessionsResponse,
  SessionAccessInfo,
  SessionConnectInfo,
} from '@/models/teaching.models';
import { grpc } from '@improbable-eng/grpc-web';

// --- Protobuf message wrappers for grpc-web ---

class GetActiveSessionsRequestMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: GetActiveSessionsRequest = {} as GetActiveSessionsRequest) {}

  serializeBinary(): Uint8Array {
    return GetActiveSessionsRequest.encode(this.value).finish();
  }

  toObject(): GetActiveSessionsRequest {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): GetActiveSessionsRequestMessage {
    return new GetActiveSessionsRequestMessage(GetActiveSessionsRequest.decode(bytes));
  }
}

class GetActiveSessionsResponseMessage implements grpc.ProtobufMessage {
  constructor(
    private readonly value: GetActiveSessionsResponse = {
      sessions: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      success: false,
      errorMessage: '',
    }
  ) {}

  serializeBinary(): Uint8Array {
    return GetActiveSessionsResponse.encode(this.value).finish();
  }

  toObject(): GetActiveSessionsResponse {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): GetActiveSessionsResponseMessage {
    return new GetActiveSessionsResponseMessage(GetActiveSessionsResponse.decode(bytes));
  }
}

class JoinSessionRequestMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: JoinSessionRequest = {} as JoinSessionRequest) {}

  serializeBinary(): Uint8Array {
    return JoinSessionRequest.encode(this.value).finish();
  }

  toObject(): JoinSessionRequest {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): JoinSessionRequestMessage {
    return new JoinSessionRequestMessage(JoinSessionRequest.decode(bytes));
  }
}

class SessionConnectResponseMessage implements grpc.ProtobufMessage {
  constructor(
    private readonly value: SessionConnectResponse = {
      streamSessionId: '',
      roomId: '',
      subscribeToken: '',
      wsUrl: '',
      success: false,
      errorMessage: '',
    }
  ) {}

  serializeBinary(): Uint8Array {
    return SessionConnectResponse.encode(this.value).finish();
  }

  toObject(): SessionConnectResponse {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): SessionConnectResponseMessage {
    return new SessionConnectResponseMessage(SessionConnectResponse.decode(bytes));
  }
}

class LeaveSessionRequestMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: LeaveSessionRequest = {} as LeaveSessionRequest) {}

  serializeBinary(): Uint8Array {
    return LeaveSessionRequest.encode(this.value).finish();
  }

  toObject(): LeaveSessionRequest {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): LeaveSessionRequestMessage {
    return new LeaveSessionRequestMessage(LeaveSessionRequest.decode(bytes));
  }
}

class LeaveSessionResponseMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: LeaveSessionResponse = { left: false, errorMessage: '' }) {}

  serializeBinary(): Uint8Array {
    return LeaveSessionResponse.encode(this.value).finish();
  }

  toObject(): LeaveSessionResponse {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): LeaveSessionResponseMessage {
    return new LeaveSessionResponseMessage(LeaveSessionResponse.decode(bytes));
  }
}

class CheckAccessRequestMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: CheckAccessRequest = {} as CheckAccessRequest) {}

  serializeBinary(): Uint8Array {
    return CheckAccessRequest.encode(this.value).finish();
  }

  toObject(): CheckAccessRequest {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): CheckAccessRequestMessage {
    return new CheckAccessRequestMessage(CheckAccessRequest.decode(bytes));
  }
}

class AccessResponseMessage implements grpc.ProtobufMessage {
  constructor(
    private readonly value: AccessResponse = {
      canJoin: false,
      canPublish: false,
      canAdminStop: false,
      reason: '',
      success: false,
      errorMessage: '',
    }
  ) {}

  serializeBinary(): Uint8Array {
    return AccessResponse.encode(this.value).finish();
  }

  toObject(): AccessResponse {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): AccessResponseMessage {
    return new AccessResponseMessage(AccessResponse.decode(bytes));
  }
}

// --- Method descriptors ---

const getActiveSessionsDescriptor: grpc.UnaryMethodDefinition<
  GetActiveSessionsRequestMessage,
  GetActiveSessionsResponseMessage
> = {
  methodName: TeachingOrchestrationServiceDefinition.methods.getActiveSessions.name,
  service: { serviceName: TeachingOrchestrationServiceDefinition.fullName },
  requestStream: false,
  responseStream: false,
  requestType: GetActiveSessionsRequestMessage,
  responseType: GetActiveSessionsResponseMessage,
};

const joinSessionDescriptor: grpc.UnaryMethodDefinition<
  JoinSessionRequestMessage,
  SessionConnectResponseMessage
> = {
  methodName: TeachingOrchestrationServiceDefinition.methods.joinSession.name,
  service: { serviceName: TeachingOrchestrationServiceDefinition.fullName },
  requestStream: false,
  responseStream: false,
  requestType: JoinSessionRequestMessage,
  responseType: SessionConnectResponseMessage,
};

const leaveSessionDescriptor: grpc.UnaryMethodDefinition<
  LeaveSessionRequestMessage,
  LeaveSessionResponseMessage
> = {
  methodName: TeachingOrchestrationServiceDefinition.methods.leaveSession.name,
  service: { serviceName: TeachingOrchestrationServiceDefinition.fullName },
  requestStream: false,
  responseStream: false,
  requestType: LeaveSessionRequestMessage,
  responseType: LeaveSessionResponseMessage,
};

const checkAccessDescriptor: grpc.UnaryMethodDefinition<
  CheckAccessRequestMessage,
  AccessResponseMessage
> = {
  methodName: TeachingOrchestrationServiceDefinition.methods.checkAccess.name,
  service: { serviceName: TeachingOrchestrationServiceDefinition.fullName },
  requestStream: false,
  responseStream: false,
  requestType: CheckAccessRequestMessage,
  responseType: AccessResponseMessage,
};

// --- Helper to execute unary gRPC calls ---

function grpcUnary<TReq extends grpc.ProtobufMessage, TRes extends grpc.ProtobufMessage>(
  descriptor: grpc.UnaryMethodDefinition<TReq, TRes>,
  host: string,
  request: TReq
): Promise<TRes> {
  return new Promise((resolve, reject) => {
    grpc.unary(descriptor, {
      host,
      request,
      onEnd: (output) => {
        if (output.status !== grpc.Code.OK) {
          reject(new Error(output.statusMessage || `gRPC error (${output.status})`));
          return;
        }
        if (!output.message) {
          reject(new Error('Empty gRPC response'));
          return;
        }
        resolve(output.message as TRes);
      },
    });
  });
}

// --- Mapping helpers ---

function mapActiveSessionInfo(proto: ProtoActiveSessionInfo): ActiveSessionInfo {
  return {
    streamSessionId: proto.streamSessionId,
    labId: proto.labId,
    labName: proto.labName,
    streamTitle: proto.streamTitle,
    presenterUserId: proto.presenterUserId,
    presenterName: proto.presenterName,
    startedAt: proto.startedAt,
    observersCount: proto.observersCount,
    deviceId: proto.deviceId,
  };
}

// --- Service class ---

class TeachingOrchestrationService {
  private grpcUrl: string | null = null;

  initialize(grpcUrl: string): void {
    this.grpcUrl = grpcUrl;
    grpc.setDefaultTransport(grpc.FetchReadableStreamTransport({}));
  }

  private getHost(): string {
    if (!this.grpcUrl) {
      throw new Error('TeachingOrchestrationService not initialized. Call initialize() first.');
    }
    return this.grpcUrl;
  }

  async getActiveSessions(params: ActiveSessionsParams): Promise<ActiveSessionsResponse> {
    const request: GetActiveSessionsRequest = {
      userId: params.userId,
      labId: params.labId ?? '',
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      institutionId: params.institutionId,
    };
    const response = await grpcUnary(
      getActiveSessionsDescriptor,
      this.getHost(),
      new GetActiveSessionsRequestMessage(request)
    );

    const data = (response as unknown as GetActiveSessionsResponseMessage).toObject();
    return {
      sessions: data.sessions.map(mapActiveSessionInfo),
      totalCount: data.totalCount,
      totalPages: data.totalPages,
      currentPage: data.currentPage,
    };
  }

  async joinSession(
    institutionId: string,
    streamSessionId: string,
    deviceId: string,
    userId: string
  ): Promise<SessionConnectInfo> {
    const request: JoinSessionRequest = {
      streamSessionId,
      deviceId,
      userId,
      institutionId,
    };

    const response = await grpcUnary(
      joinSessionDescriptor,
      this.getHost(),
      new JoinSessionRequestMessage(request)
    );

    const data = (response as unknown as SessionConnectResponseMessage).toObject();
    return {
      streamSessionId: data.streamSessionId,
      roomId: data.roomId,
      subscribeToken: data.subscribeToken,
      wsUrl: data.wsUrl,
      success: data.success,
      errorMessage: data.errorMessage,
    };
  }

  async leaveSession(
    institutionId: string,
    streamSessionId: string,
    userId: string
  ): Promise<void> {
    const request: LeaveSessionRequest = { streamSessionId, userId, institutionId };

    await grpcUnary(
      leaveSessionDescriptor,
      this.getHost(),
      new LeaveSessionRequestMessage(request)
    );
  }

  async checkAccess(
    institutionId: string,
    userId: string,
    deviceId: string
  ): Promise<SessionAccessInfo> {
    const request: CheckAccessRequest = { userId, deviceId, institutionId };

    const response = await grpcUnary(
      checkAccessDescriptor,
      this.getHost(),
      new CheckAccessRequestMessage(request)
    );

    const data = (response as unknown as AccessResponseMessage).toObject();
    return {
      canJoin: data.canJoin,
      canPublish: data.canPublish,
      canAdminStop: data.canAdminStop,
      reason: data.reason,
    };
  }
}

export const teachingOrchestrationService = new TeachingOrchestrationService();
